import { Injectable } from '@angular/core';
import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { AlertController, ToastController } from '@ionic/angular';

export interface PermissionStatus {
  granted: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  constructor(
    private alertController: AlertController,
    private toastController: ToastController
  ) { }

  /**
   * Verifica si la plataforma es nativa (Android/iOS)
   */
  private isNativePlatform(): boolean {
    return Capacitor.getPlatform() !== 'web';
  }

  /**
   * Solicita permisos de cámara
   * @returns Promise<PermissionStatus>
   */
  async requestCameraPermission(): Promise<PermissionStatus> {
    if (!this.isNativePlatform()) {
      return { granted: true };
    }

    try {
      const status = await Camera.checkPermissions();
      
      if (status?.camera === 'granted' || status?.camera === 'limited') {
        return { granted: true };
      }

      if (status?.camera === 'denied') {
        await this.showPermissionDeniedAlert('Cámara', 
          'La aplicación necesita acceso a la cámara para tomar fotos de evidencias.');
        return { granted: false, message: 'Permiso de cámara denegado' };
      }

      const requested = await Camera.requestPermissions({ permissions: ['camera'] });
      
      if (requested?.camera === 'granted' || requested?.camera === 'limited') {
        return { granted: true };
      }

      return { granted: false, message: 'Permiso de cámara no otorgado' };
    } catch (error) {
      console.error('Error al solicitar permiso de cámara:', error);
      return { granted: false, message: 'Error al solicitar permiso de cámara' };
    }
  }

  /**
   * Solicita permisos de geolocalización
   * @param showAlert Si debe mostrar alerta al usuario
   * @returns Promise<PermissionStatus>
   */
  async requestLocationPermission(showAlert: boolean = true): Promise<PermissionStatus> {
    if (!this.isNativePlatform()) {
      return { granted: true };
    }

    try {
      const status = await Geolocation.checkPermissions();
      
      if (status?.location === 'granted') {
        return { granted: true };
      }

      if (status?.location === 'denied') {
        if (showAlert) {
          await this.showPermissionDeniedAlert('Ubicación', 
            'La aplicación necesita acceso a la ubicación GPS para registrar la geolocalización de las evaluaciones.');
        }
        return { granted: false, message: 'Permiso de ubicación denegado' };
      }

      const requested = await Geolocation.requestPermissions();
      
      if (requested?.location === 'granted') {
        return { granted: true };
      }

      if (showAlert) {
        await this.showPermissionDeniedAlert('Ubicación', 
          'Es necesario permitir el acceso al GPS del dispositivo para continuar.');
      }

      return { granted: false, message: 'Permiso de ubicación no otorgado' };
    } catch (error) {
      console.error('Error al solicitar permiso de ubicación:', error);
      
      if (showAlert) {
        await this.showToast('Error al solicitar permiso de ubicación', 'danger');
      }
      
      return { granted: false, message: 'Error al solicitar permiso de ubicación' };
    }
  }

  /**
   * Solicita permisos de notificaciones (Android 13+)
   * @returns Promise<PermissionStatus>
   */
  async requestNotificationPermission(): Promise<PermissionStatus> {
    if (!this.isNativePlatform()) {
      return { granted: true };
    }

    // Para Android 13+ se necesita solicitar el permiso POST_NOTIFICATIONS
    // Esto se maneja automáticamente por Capacitor cuando se intenta mostrar una notificación
    // Por ahora retornamos true ya que no hay un plugin específico instalado
    return { granted: true };
  }

  /**
   * Verifica todos los permisos necesarios al inicio de la app
   */
  async checkAllPermissions(): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    // Verificar permisos de ubicación silenciosamente
    await this.requestLocationPermission(false);
    
    // No verificamos cámara aquí porque se solicita cuando el usuario intenta tomar una foto
  }

  /**
   * Muestra una alerta cuando un permiso ha sido denegado
   */
  private async showPermissionDeniedAlert(permissionName: string, message: string): Promise<void> {
    const alert = await this.alertController.create({
      header: `Permiso de ${permissionName} Requerido`,
      message: `${message}\n\nPor favor, habilita el permiso en la configuración de la aplicación.`,
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel'
        },
        {
          text: 'Ir a Configuración',
          handler: () => {
            // En una implementación completa, aquí se abriría la configuración de la app
            // Esto requeriría un plugin adicional como @capacitor/app-launcher
            this.showToast('Por favor, habilita los permisos en la configuración del dispositivo', 'warning');
          }
        }
      ]
    });

    await alert.present();
  }

  /**
   * Muestra un toast con un mensaje
   */
  private async showToast(message: string, color: string = 'dark'): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      position: 'bottom',
      color
    });

    await toast.present();
  }
}
